import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useAuth } from "../../../../auth";
import { useQuery } from "@tanstack/react-query";
import { customInstanceDocumentos } from "../../../../api/axios-client";
import { decodeNavParams } from "../../../../utils/navParams";
import { z } from "zod";

const logSearchSchema = z.object({
    from: z.string().optional(),
    fromParams: z.string().optional(),
});
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    Skeleton,
    Grid,
    Card,
    CardContent,
    useTheme,
    useMediaQuery,
    Stack,
    Divider,
    IconButton,
} from "@mui/material";
import { ArrowBack, Person } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute('/__auth/documents/$documentId/log')({
    component: DocumentLogComponent,
    validateSearch: (search) => logSearchSchema.parse(search),
    staticData: { title: "routes.documentHistory" }
});

function DocumentLogComponent() {
    const { t } = useTranslation();
    const { documentId } = Route.useParams();
    const navigate = useNavigate();
    const search = Route.useSearch();
    const { user } = useAuth();
    const tenantId = user?.tenant || "default-domain";
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { data: document, isLoading: isLoadingDocument } = useQuery({
        queryKey: ['document-detail', tenantId, documentId],
        queryFn: () => customInstanceDocumentos<any>(
            `/${tenantId}/documents/${documentId}`,
            { method: 'GET' }
        ),
        enabled: !!documentId && !!tenantId
    });

    const { data: historyData, isLoading: isLoadingHistory, error } = useQuery({
        queryKey: ['document-history', tenantId, documentId],
        queryFn: () => customInstanceDocumentos<any>(
            `/${tenantId}/documents/${documentId}/history?sort=createdAt,desc&size=100`,
            { method: 'GET' }
        ),
        enabled: !!documentId && !!tenantId
    });

    const logs = historyData?.content || [];
    const isLoading = isLoadingDocument || isLoadingHistory;

    const formatDate = (dateString?: string) => {
        if (!dateString) return "-";
        return new Date(dateString).toLocaleString("pt-BR");
    };

    const renderMobileView = () => (
        <Stack spacing={2}>
            {isLoading ? (
                Array.from(new Array(3)).map((_, index) => (
                    <Card key={index}>
                        <CardContent>
                            <Skeleton variant="text" width="60%" height={24} />
                            <Skeleton variant="text" width="40%" />
                            <Skeleton variant="text" width="80%" />
                        </CardContent>
                    </Card>
                ))
            ) : logs.length === 0 ? (
                <Typography color="textSecondary" align="center" py={3}>
                    Nenhum registro encontrado.
                </Typography>
            ) : (
                logs.map((log: any) => (
                    <Card key={log.id} variant="outlined">
                        <CardContent>
                            <Grid container spacing={1}>
                                <Grid size={{ xs: 12 }}>
                                    <Typography variant="caption" color="textSecondary">
                                        {formatDate(log.createdAt)}
                                    </Typography>
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <Typography variant="body2" fontWeight="bold">
                                        {log.operation || t("documents.unknownOperation")}
                                    </Typography>
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Person fontSize="small" sx={{ color: 'text.secondary', fontSize: 16 }} />
                                        <Typography variant="body2">
                                            {log.userId || "-"}
                                        </Typography>
                                    </Stack>
                                </Grid>
                                {log.description && (
                                    <Grid size={{ xs: 12 }}>
                                        <Typography variant="body2" color="textSecondary" sx={{ mt: 1, backgroundColor: 'action.hover', p: 1, borderRadius: 1 }}>
                                            {log.description}
                                        </Typography>
                                    </Grid>
                                )}
                            </Grid>
                        </CardContent>
                    </Card>
                ))
            )}
        </Stack>
    );

    const renderDesktopView = () => (
        <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)' }}>
            <Table>
                <TableHead>
                    <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                        <TableCell sx={{ fontWeight: 600, color: '#495057' }}>Data/Hora</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#495057' }}>Usuário</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#495057' }}>Operação</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#495057' }}>Descrição</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {isLoading ? (
                        Array.from(new Array(5)).map((_, index) => (
                            <TableRow key={index}>
                                <TableCell><Skeleton width={150} /></TableCell>
                                <TableCell><Skeleton width={100} /></TableCell>
                                <TableCell><Skeleton width={100} /></TableCell>
                                <TableCell><Skeleton width={200} /></TableCell>
                            </TableRow>
                        ))
                    ) : logs.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={4} align="center">
                                <Typography color="textSecondary" py={3}>
                                    Nenhum registro encontrado.
                                </Typography>
                            </TableCell>
                        </TableRow>
                    ) : (
                        logs.map((log: any) => (
                            <TableRow key={log.id} hover>
                                <TableCell>{formatDate(log.createdAt)}</TableCell>
                                <TableCell>{log.userId || "-"}</TableCell>
                                <TableCell>{log.operation || "-"}</TableCell>
                                <TableCell>{log.description || "-"}</TableCell>
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );

    const handleBack = () => {
        if (search.from === 'analysis' && search.fromParams) {
            const params = decodeNavParams(search.fromParams) as any;
            navigate({
                to: '/documents/analysis',
                search: params
            });
        } else {
            navigate({ to: '..' });
        }
    };

    return (
        <Stack spacing={3} p={3}>
            <Stack direction="row" alignItems="center" spacing={2}>
                <IconButton onClick={handleBack} color="primary">
                    <ArrowBack />
                </IconButton>
                <Typography variant="h4">Histórico do Documento</Typography>
            </Stack>

            <Paper sx={{ p: 3, borderRadius: 2 }}>
                {isLoadingDocument ? (
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 6 }}><Skeleton width="60%" /></Grid>
                        <Grid size={{ xs: 12, md: 6 }}><Skeleton width="40%" /></Grid>
                    </Grid>
                ) : (
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Typography variant="subtitle2" color="textSecondary">Nome do Documento</Typography>
                            <Typography variant="h6" color="primary">{document?.name || "-"}</Typography>
                        </Grid>
                        <Grid size={{ xs: 6, md: 3 }}>
                            <Typography variant="subtitle2" color="textSecondary">ID</Typography>
                            <Typography variant="body1">{document?.id}</Typography>
                        </Grid>
                        <Grid size={{ xs: 6, md: 3 }}>
                            <Typography variant="subtitle2" color="textSecondary">Extensão</Typography>
                            <Typography variant="body1" sx={{ textTransform: 'uppercase' }}>
                                {document?.extension?.name || document?.name?.split('.').pop() || "-"}
                            </Typography>
                        </Grid>
                    </Grid>
                )}
            </Paper>

            <Divider />

            {error ? (
                <Typography color="error">Erro ao carregar histórico.</Typography>
            ) : (
                isMobile ? renderMobileView() : renderDesktopView()
            )}
        </Stack>
    );
}
